/**
 * SKI AXIS · /axis-index/cohort1/intake — Bronze v0 Questionnaire
 *
 * Cohort-1-gated questionnaire. 10-screen flow:
 *   1. Code entry / verify
 *   2. Identity confirmation + Founder Charter acceptance
 *   3. Stage Tag selection (locked at submission)
 *   4-8. Five dimension panels (6 questions each)
 *   9. Review + honesty declaration
 *   10. Confirmation + scorecard preview link
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, ArrowLeft, CheckCircle2, AlertCircle, Loader2, Lock, ShieldCheck,
} from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import useSEO from '@/hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = `
.bv-root{background:#FAF7F0;color:#0D1B2A;min-height:100vh;font-family:'DM Sans',-apple-system,sans-serif;}
.bv-shell{max-width:880px;margin:0 auto;padding:48px 28px;}
@media(max-width:680px){.bv-shell{padding:28px 18px;}}

.bv-eb{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;display:flex;align-items:center;gap:14px;margin-bottom:18px;}
.bv-eb::before{content:'';width:32px;height:1px;background:#C8A84B;}

.bv-progress{height:3px;background:#E8DFC9;margin-bottom:34px;position:relative;}
.bv-progress-fill{height:100%;background:linear-gradient(90deg,#C8A84B 0%,#D9BC6A 100%);transition:width .35s ease;}
.bv-step{font-size:11px;font-weight:700;letter-spacing:.22em;color:#3D4A5C;text-transform:uppercase;margin-bottom:10px;}

.bv-h1{font-family:'Cormorant Display',serif;font-size:clamp(32px,4.5vw,48px);font-weight:700;line-height:1.05;letter-spacing:-.018em;margin:0 0 14px;color:#0D1B2A;}
.bv-h1 em{font-style:italic;color:#C8A84B;}
.bv-sub{font-size:15.5px;color:#3D4A5C;line-height:1.6;max-width:680px;font-weight:300;margin:0 0 28px;}

.bv-card{background:#fff;border:1px solid #E8DFC9;border-left:4px solid #C8A84B;padding:32px 36px;margin-bottom:14px;}
@media(max-width:680px){.bv-card{padding:24px 22px;}}
.bv-fld{display:flex;flex-direction:column;gap:6px;margin-bottom:14px;}
.bv-lbl{font-size:11px;font-weight:700;letter-spacing:.18em;color:#3D4A5C;text-transform:uppercase;}
.bv-input{font-family:inherit;font-size:14.5px;color:#0D1B2A;background:#FAF7F0;border:1px solid #E8DFC9;padding:14px 16px;outline:none;letter-spacing:.04em;}
.bv-input:focus{border-color:#C8A84B;box-shadow:0 0 0 3px rgba(200,168,75,.08);}

.bv-q{padding:18px 0;border-bottom:1px solid #FAF0DC;}
.bv-q:last-child{border-bottom:0;}
.bv-q-num{font-size:10.5px;font-weight:800;letter-spacing:.22em;color:#C8A84B;text-transform:uppercase;margin-bottom:6px;}
.bv-q-text{font-size:15.5px;color:#0D1B2A;line-height:1.5;margin:0 0 14px;font-weight:500;}
.bv-likert{display:flex;flex-direction:column;gap:6px;}
.bv-opt{display:flex;align-items:flex-start;gap:12px;padding:11px 14px;border:1px solid #E8DFC9;background:#FAF7F0;cursor:pointer;transition:all .12s;font-size:13.5px;color:#0D1B2A;}
.bv-opt:hover{border-color:#C8A84B;background:#FBF8EE;}
.bv-opt.sel{border-color:#C8A84B;background:#FBF8EE;border-left:3px solid #C8A84B;font-weight:600;}
.bv-opt input{margin-top:3px;accent-color:#C8A84B;}
.bv-opt-num{font-family:'Cormorant Display',serif;font-style:italic;font-weight:700;color:#C8A84B;font-size:14px;width:22px;}
.bv-opt-lbl{flex:1;line-height:1.5;}

.bv-dim-eb{font-size:10.5px;letter-spacing:.28em;font-weight:800;color:#C8A84B;text-transform:uppercase;margin-bottom:8px;}
.bv-dim-name{font-family:'Cormorant Display',serif;font-size:30px;font-weight:700;color:#0D1B2A;line-height:1.05;letter-spacing:-.012em;margin:0 0 8px;}
.bv-dim-name em{font-style:italic;color:#C8A84B;}
.bv-dim-tag{font-size:14px;color:#3D4A5C;font-style:italic;font-family:'Cormorant Display',serif;font-size:16px;margin:0 0 22px;line-height:1.45;}

.bv-stage-list{display:grid;grid-template-columns:1fr;gap:8px;margin-top:8px;}
.bv-stage{padding:14px 18px;border:1px solid #E8DFC9;background:#FAF7F0;cursor:pointer;display:flex;align-items:center;gap:14px;transition:all .12s;}
.bv-stage:hover{border-color:#C8A84B;background:#FBF8EE;}
.bv-stage.sel{border-color:#C8A84B;background:#FBF8EE;border-left:3px solid #C8A84B;}
.bv-stage input{accent-color:#C8A84B;}
.bv-stage-lbl{font-size:14px;color:#0D1B2A;font-weight:500;line-height:1.45;}

.bv-charter{background:linear-gradient(135deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;padding:28px 32px;margin-bottom:14px;border-left:4px solid #C8A84B;}
.bv-charter-eb{font-size:10.5px;font-weight:700;letter-spacing:.28em;color:#C8A84B;text-transform:uppercase;margin-bottom:10px;}
.bv-charter-h{font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;color:#FAF7F0;margin:0 0 12px;line-height:1.2;}
.bv-charter-h em{font-style:italic;color:#C8A84B;}
.bv-charter ul{margin:0;padding:0;list-style:none;}
.bv-charter li{font-size:13px;color:rgba(250,247,240,.84);line-height:1.6;padding:5px 0 5px 18px;position:relative;border-top:1px solid rgba(200,168,75,.16);}
.bv-charter li:first-child{border-top:0;}
.bv-charter li::before{content:'';width:7px;height:7px;background:#C8A84B;border-radius:50%;position:absolute;left:0;top:11px;}

.bv-checkbox-row{display:flex;align-items:flex-start;gap:12px;padding:14px 16px;background:#FBF8EE;border:1px solid #E8DFC9;border-left:3px solid #C8A84B;}
.bv-checkbox-row input{margin-top:3px;accent-color:#C8A84B;width:16px;height:16px;}
.bv-checkbox-row label{font-size:13.5px;color:#0D1B2A;line-height:1.55;flex:1;cursor:pointer;}

.bv-cta-row{display:flex;justify-content:space-between;align-items:center;gap:12px;margin-top:20px;flex-wrap:wrap;}
.bv-back{display:inline-flex;align-items:center;gap:8px;color:#3D4A5C;background:transparent;border:none;font-family:inherit;font-size:11.5px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;cursor:pointer;padding:14px 8px;}
.bv-back:hover{color:#0D1B2A;}
.bv-fwd{display:inline-flex;align-items:center;gap:10px;background:#0D1B2A;color:#C8A84B;border:1px solid #0D1B2A;padding:14px 28px;font-family:inherit;font-size:11.5px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;cursor:pointer;transition:all .15s;}
.bv-fwd:hover:not(:disabled){background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.bv-fwd:disabled{opacity:.4;cursor:not-allowed;}

.bv-err{background:rgba(173,98,64,.08);border-left:3px solid #ad6240;color:#ad6240;padding:12px 16px;margin-bottom:14px;font-size:13.5px;display:flex;align-items:center;gap:10px;}

.bv-rev-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin-top:14px;}
@media(max-width:680px){.bv-rev-grid{grid-template-columns:repeat(2,1fr);}}
.bv-rev{background:#FBF8EE;border:1px solid #E8DFC9;border-top:3px solid #C8A84B;padding:14px 12px;text-align:center;}
.bv-rev-code{font-size:10px;letter-spacing:.22em;color:#8A6A14;font-weight:800;text-transform:uppercase;}
.bv-rev-name{font-family:'Cormorant Display',serif;font-size:14px;font-weight:700;color:#0D1B2A;margin:4px 0;line-height:1.18;}
.bv-rev-cnt{font-family:'Cormorant Display',serif;font-style:italic;color:#C8A84B;font-weight:700;font-size:18px;}
`;

// =================== STEP 1 · CODE ENTRY ===================
const StepCode = ({ initialCode, onVerified, onError }) => {
  const navigate = useNavigate();
  const [code, setCode] = useState(initialCode || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (code.trim().length < 4) { setErr('Please enter your invitation code.'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/axis-index/cohort1/verify`, { code: code.trim() });
      onVerified({ code: code.trim(), ...data });
    } catch (ex) {
      // Already-submitted: redirect to existing scorecard
      const detail = ex?.response?.data?.detail;
      if (ex?.response?.status === 409 && typeof detail === 'object' && detail?.session_id) {
        navigate(`/axis-index/cohort1/scorecard/${detail.session_id}`);
        return;
      }
      const msg = (typeof detail === 'string' ? detail : detail?.message)
        || 'Could not verify the code. Try again or email Nitish.';
      setErr(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="bv-card" data-testid="bv-step-code">
      <div className="bv-eb"><Lock className="h-3 w-3" /> Cohort 1 · Internal Bronze · Confidential</div>
      <h1 className="bv-h1">Welcome to your <em>Axis Index</em> intake.</h1>
      <p className="bv-sub">
        Please enter the personal invitation code from your Cohort 1 onboarding email.
        The questionnaire takes ~30 minutes. Your responses are held confidentially.
      </p>
      <form onSubmit={submit}>
        {err && <div className="bv-err"><AlertCircle className="h-4 w-4" /><span>{err}</span></div>}
        <div className="bv-fld">
          <label className="bv-lbl">Invitation code</label>
          <input className="bv-input" data-testid="bv-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. abc123XYZ" autoFocus />
        </div>
        <div className="bv-cta-row">
          <span></span>
          <button className="bv-fwd" disabled={busy} type="submit" data-testid="bv-code-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {busy ? 'Verifying…' : 'Continue'}
            {!busy && <ArrowRight className="h-4 w-4" />}
          </button>
        </div>
      </form>
    </div>
  );
};

// =================== STEP 2 · CHARTER ===================
const StepCharter = ({ session, onContinue, onBack }) => (
  <>
    <div className="bv-card" data-testid="bv-step-charter">
      <div className="bv-eb"><CheckCircle2 className="h-3 w-3" /> Identity confirmed</div>
      <h1 className="bv-h1">Hello, <em>{session.founder_name?.split(' ')[0]}</em>.</h1>
      <p className="bv-sub">
        We have you registered for <strong>{session.cohort}</strong>{session.company ? ` · ${session.company}` : ''}.
        Before we begin, please read the Founder Charter — your six rights through this assessment.
      </p>
    </div>
    <div className="bv-charter">
      <div className="bv-charter-eb">Founder Charter</div>
      <h2 className="bv-charter-h">You retain agency at <em>every step</em>.</h2>
      <ul>
        <li>Your Bronze score is held confidentially through October 2026, and beyond unless you opt in.</li>
        <li>You may withdraw your data from the assessment at any time. No questions, no penalty.</li>
        <li>The 2027 Annual Report uses Cohort 1 data anonymised by default. Named case studies require your separate written consent at Week 6.</li>
        <li>You retain a copy of your scorecard, your evidence, and your improvement deltas.</li>
        <li>No founder data is shared with third parties (investors, free zones, media) without your explicit written consent.</li>
        <li>Documents and responses are encrypted at rest (AES-256) and in transit (TLS 1.3). Role-restricted access. Assessor NDAs.</li>
      </ul>
    </div>
    <div className="bv-cta-row">
      <button className="bv-back" onClick={onBack}><ArrowLeft className="h-4 w-4" /> Back</button>
      <button className="bv-fwd" onClick={onContinue} data-testid="bv-charter-continue">
        I understand · continue <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  </>
);

// =================== STEP 3 · STAGE TAG ===================
const StepStage = ({ instrument, value, onChange, onContinue, onBack }) => (
  <>
    <div className="bv-card" data-testid="bv-step-stage">
      <div className="bv-eb">Stage Tag · Locked at submission</div>
      <h1 className="bv-h1">What stage is your company at <em>today</em>?</h1>
      <p className="bv-sub">
        Pick the stage that best reflects your company's current operating reality. Your Stage Tag is locked at submission and cannot be retroactively re-cast.
        This protects you — your Pre-Seed score will never be evaluated against Series A expectations.
      </p>
      <div className="bv-stage-list">
        {instrument.stage_tags.map((s) => (
          <label key={s.value} className={`bv-stage ${value === s.value ? 'sel' : ''}`} data-testid={`bv-stage-${s.value}`}>
            <input type="radio" name="stage" value={s.value} checked={value === s.value} onChange={() => onChange(s.value)} />
            <span className="bv-stage-lbl">{s.label}</span>
          </label>
        ))}
      </div>
    </div>
    <div className="bv-cta-row">
      <button className="bv-back" onClick={onBack}><ArrowLeft className="h-4 w-4" /> Back</button>
      <button className="bv-fwd" disabled={!value} onClick={onContinue} data-testid="bv-stage-continue">
        Continue <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  </>
);

// =================== STEP 4–8 · DIMENSION PANEL ===================
const StepDimension = ({ dim, instrument, responses, onChange, onContinue, onBack }) => {
  const allAnswered = dim.questions.every((q) => responses[q.id] !== undefined);
  return (
    <>
      <div className="bv-card" data-testid={`bv-step-dim-${dim.code}`}>
        <div className="bv-dim-eb">Dimension {dim.code.replace('D', '')} · {dim.questions.length} questions</div>
        <h1 className="bv-dim-name">{dim.name.split(' ').slice(0, -1).join(' ')} <em>{dim.name.split(' ').slice(-1)}</em></h1>
        <p className="bv-dim-tag">{dim.tagline}</p>
        {dim.questions.map((q, idx) => (
          <div key={q.id} className="bv-q" data-testid={`bv-q-${q.id}`}>
            <div className="bv-q-num">Q{idx + 1} · {q.id}</div>
            <p className="bv-q-text">{q.text}</p>
            <div className="bv-likert">
              {instrument.likert_options.map((opt) => (
                <label key={opt.value} className={`bv-opt ${responses[q.id] === opt.value ? 'sel' : ''}`} data-testid={`bv-opt-${q.id}-${opt.value}`}>
                  <input type="radio" name={q.id} checked={responses[q.id] === opt.value} onChange={() => onChange(q.id, opt.value)} />
                  <span className="bv-opt-num">{opt.value}</span>
                  <span className="bv-opt-lbl">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="bv-cta-row">
        <button className="bv-back" onClick={onBack}><ArrowLeft className="h-4 w-4" /> Back</button>
        <button className="bv-fwd" disabled={!allAnswered} onClick={onContinue} data-testid={`bv-dim-${dim.code}-continue`}>
          {allAnswered ? 'Continue' : `Answer all ${dim.questions.length} to continue`} <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </>
  );
};

// =================== STEP 9 · REVIEW + DECLARATION ===================
const StepReview = ({ instrument, responses, stageTag, onSubmit, onBack, busy, error, declared, setDeclared }) => {
  const completion = useMemo(() => {
    const out = {};
    instrument.dimensions.forEach((d) => {
      out[d.code] = d.questions.filter((q) => responses[q.id] !== undefined).length;
    });
    return out;
  }, [instrument, responses]);
  const stageLabel = instrument.stage_tags.find((s) => s.value === stageTag)?.label || stageTag;

  return (
    <>
      <div className="bv-card" data-testid="bv-step-review">
        <div className="bv-eb"><CheckCircle2 className="h-3 w-3" /> Review · Sign · Submit</div>
        <h1 className="bv-h1">Ready to <em>submit</em>?</h1>
        <p className="bv-sub">
          Your responses lock at submission. Your Stage Tag is permanent. You retain a copy of your scorecard.
        </p>
        <div className="bv-fld">
          <label className="bv-lbl">Stage Tag</label>
          <div style={{ fontSize: 15, color: '#0D1B2A', fontStyle: 'italic', fontFamily: 'Cormorant Display, serif' }}>{stageLabel}</div>
        </div>
        <div className="bv-fld">
          <label className="bv-lbl">Coverage by dimension</label>
          <div className="bv-rev-grid">
            {instrument.dimensions.map((d) => (
              <div key={d.code} className="bv-rev">
                <div className="bv-rev-code">{d.code}</div>
                <div className="bv-rev-name">{d.name}</div>
                <div className="bv-rev-cnt">{completion[d.code]}/{d.questions.length}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bv-card">
        <div className="bv-eb">Honesty Declaration</div>
        <p className="bv-sub" style={{ marginBottom: 16 }}>
          The Axis Index is a measurable instrument. Honest self-assessment is the foundation of useful diagnosis.
          You may be at any stage — that is fine. Misrepresenting your stage is not.
        </p>
        <div className="bv-checkbox-row">
          <input type="checkbox" id="declared" checked={declared} onChange={(e) => setDeclared(e.target.checked)} data-testid="bv-declaration" />
          <label htmlFor="declared">
            I confirm that my responses honestly reflect my company's current governance posture, and I understand that my Stage Tag and responses cannot be modified after submission.
          </label>
        </div>
      </div>

      {error && <div className="bv-err"><AlertCircle className="h-4 w-4" /><span>{error}</span></div>}

      <div className="bv-cta-row">
        <button className="bv-back" onClick={onBack} disabled={busy}><ArrowLeft className="h-4 w-4" /> Back</button>
        <button className="bv-fwd" disabled={!declared || busy} onClick={onSubmit} data-testid="bv-final-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          {busy ? 'Submitting…' : 'Sign & submit'}
          {!busy && <ArrowRight className="h-4 w-4" />}
        </button>
      </div>
    </>
  );
};

// =================== MAIN ===================
export default function AxisIndexCohort1IntakePage() {
  useSEO({
    title: 'Cohort 1 · Bronze Intake · The Axis Index™ | SKI Axis',
    description: 'Cohort 1 confidential Bronze v0 questionnaire. Invitation-only.',
  });
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [instrument, setInstrument] = useState(null);
  const [step, setStep] = useState(0);
  const [session, setSession] = useState(null);
  const [stageTag, setStageTag] = useState('');
  const [responses, setResponses] = useState({});
  const [declared, setDeclared] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Load instrument on mount
  useEffect(() => {
    let live = true;
    axios.get(`${API}/axis-index/cohort1/questionnaire`).then((r) => {
      if (live) setInstrument(r.data);
    }).catch((e) => {
      console.error('Failed to load questionnaire', e);
    });
    return () => { live = false; };
  }, []);

  // Auto-fill code from URL ?code=
  const initialCode = params.get('code') || '';

  if (!instrument) {
    return (
      <div className="bv-root">
        <style>{STYLES}</style>
        <Header />
        <div className="bv-shell" style={{ textAlign: 'center', paddingTop: 120 }}>
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#C8A84B', margin: '0 auto' }} />
          <p style={{ marginTop: 14, color: '#3D4A5C' }}>Loading the instrument…</p>
        </div>
        <Footer />
      </div>
    );
  }

  const totalSteps = 4 + instrument.dimensions.length; // code, charter, stage, D1..D5, review = 9
  const progressPct = Math.round(((step) / (totalSteps)) * 100);

  const onResponseChange = (qid, val) => setResponses((p) => ({ ...p, [qid]: val }));

  const handleSubmit = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/axis-index/cohort1/submit`, {
        code: session.code,
        stage_tag: stageTag,
        responses,
        declaration_signed: true,
      });
      navigate(`/axis-index/cohort1/scorecard/${data.session_id}`);
    } catch (ex) {
      setError(ex?.response?.data?.detail || 'Submission failed. Please try again.');
    } finally { setBusy(false); }
  };

  let panel;
  if (step === 0) {
    panel = <StepCode initialCode={initialCode} onVerified={(s) => { setSession(s); setStep(1); }} />;
  } else if (step === 1) {
    panel = <StepCharter session={session} onContinue={() => setStep(2)} onBack={() => setStep(0)} />;
  } else if (step === 2) {
    panel = <StepStage instrument={instrument} value={stageTag} onChange={setStageTag} onContinue={() => setStep(3)} onBack={() => setStep(1)} />;
  } else if (step >= 3 && step <= 7) {
    const dimIdx = step - 3;
    const dim = instrument.dimensions[dimIdx];
    panel = (
      <StepDimension
        dim={dim} instrument={instrument} responses={responses} onChange={onResponseChange}
        onContinue={() => setStep(step + 1)} onBack={() => setStep(step - 1)}
      />
    );
  } else if (step === 8) {
    panel = (
      <StepReview
        instrument={instrument} responses={responses} stageTag={stageTag}
        onSubmit={handleSubmit} onBack={() => setStep(7)}
        busy={busy} error={error} declared={declared} setDeclared={setDeclared}
      />
    );
  }

  return (
    <div className="bv-root" data-testid="axis-index-cohort1-intake">
      <style>{STYLES}</style>
      <Header />
      <div className="bv-shell">
        {step > 0 && (
          <>
            <div className="bv-step">Step {step + 1} of {totalSteps + 1} · The Axis Index Bronze v0</div>
            <div className="bv-progress" data-testid="bv-progress">
              <div className="bv-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
          </>
        )}
        {panel}
      </div>
      <Footer />
    </div>
  );
}
